// /reset-password (request + sent) and /reset-password/:token (set + done, or
// expired) — the full "olvidé mi contraseña" journey. Shares the
// AuthLayout chrome with Login / Mfa. The flow never reveals whether an account
// exists (the "revisa tu correo" screen is identical either way) and never dead-
// ends (the expired state offers a fresh link). Actions go through the
// reset-password-data seam (mock).

import { type FormEvent, type ReactNode, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { AuthLayout } from './AuthLayout.js'
import {
  EXPIRED_TOKEN_SENTINEL,
  PASSWORD_RULES,
  passwordMeetsPolicy,
  requestPasswordReset,
  RESET_LINK_TTL,
  submitNewPassword,
} from './reset-password-data.js'
import { Button, FormField, Input, PasswordInput } from '../../design-system/index.js'

type View = 'request' | 'sent' | 'set' | 'done' | 'expired'

function BackToLogin(): JSX.Element {
  return (
    <p className="mt-5 text-center">
      <Link
        to="/login"
        className="inline-flex items-center gap-1.5 rounded text-[12px] font-semibold text-link hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-blue"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
          className="size-3"
        >
          <path d="m15 6-6 6 6 6" />
        </svg>
        Volver a iniciar sesión
      </Link>
    </p>
  )
}

function StateBlock({
  tone,
  icon,
  title,
  children,
}: {
  tone: 'blue' | 'ok' | 'warn'
  icon: ReactNode
  title: string
  children: ReactNode
}): JSX.Element {
  const toneClass =
    tone === 'ok'
      ? 'bg-ok-bg text-ok'
      : tone === 'warn'
        ? 'bg-warn-bg text-warn'
        : 'bg-brand-blue/10 text-link'
  return (
    <div className="text-center">
      <span
        aria-hidden
        className={`mx-auto grid size-[60px] place-items-center rounded-2xl ${toneClass}`}
      >
        {icon}
      </span>
      <h1 className="mt-4 text-[22px] font-bold tracking-[-0.022em] text-text">{title}</h1>
      {children}
    </div>
  )
}

const MAIL_ICON = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
    className="size-[30px]"
  >
    <rect x="3" y="5" width="18" height="14" rx="2.4" />
    <path d="m4 7 8 6 8-6" />
  </svg>
)

const CHECK_ICON = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
    className="size-[30px]"
  >
    <path d="M20 6 9 17l-5-5" />
  </svg>
)

const CLOCK_ICON = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.9"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
    className="size-[28px]"
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </svg>
)

/** Map the 4-rule policy onto the PasswordInput meter's 3 levels. */
function strengthLevel(pw: string): 'weak' | 'mid' | 'strong' {
  const met = PASSWORD_RULES.filter((r) => r.test(pw)).length
  return met <= 1 ? 'weak' : met === 2 ? 'mid' : 'strong'
}

export function ResetPasswordPage(): JSX.Element {
  const { token } = useParams()
  const navigate = useNavigate()

  const [view, setView] = useState<View>(() =>
    token === undefined ? 'request' : token === EXPIRED_TOKEN_SENTINEL ? 'expired' : 'set',
  )
  const [email, setEmail] = useState('')
  const [resent, setResent] = useState(false)
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)

  async function onRequest(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault()
    setBusy(true)
    try {
      await requestPasswordReset(email)
      setView('sent')
    } finally {
      setBusy(false)
    }
  }

  async function onResend(): Promise<void> {
    setBusy(true)
    try {
      await requestPasswordReset(email)
      setResent(true)
    } finally {
      setBusy(false)
    }
  }

  async function onSetPassword(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault()
    setBusy(true)
    try {
      await submitNewPassword(token ?? '', password)
      setView('done')
    } catch {
      // A 400 here means the single-use link is spent or expired — the only
      // recovery is requesting a fresh one, which is exactly what the
      // 'expired' view offers. Without this catch the rejection was silent
      // and the user sat on the form with no feedback at all.
      setView('expired')
    } finally {
      setBusy(false)
    }
  }

  const policyOk = passwordMeetsPolicy(password)
  const matches = password.length > 0 && password === confirm

  return (
    <AuthLayout>
      {view === 'request' && (
        <>
          <h1 className="text-center text-[22px] font-bold tracking-[-0.022em] text-text">
            ¿Olvidaste tu contraseña?
          </h1>
          <p className="mx-auto mt-2 max-w-[40ch] text-center text-[13px] leading-relaxed text-text-2">
            Escribe el correo de tu cuenta y te enviaremos un enlace para crear una nueva.
          </p>
          <form onSubmit={(e) => void onRequest(e)} className="mt-6" noValidate>
            <FormField label="Correo electrónico" htmlFor="reset-email">
              <Input
                type="email"
                inputMode="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={busy}
                placeholder="tu@hospital.com"
              />
            </FormField>
            <Button
              type="submit"
              block
              className="mt-5"
              loading={busy}
              disabled={email.length === 0}
            >
              Enviar enlace
            </Button>
          </form>
          <BackToLogin />
        </>
      )}

      {view === 'sent' && (
        <>
          <StateBlock tone="blue" icon={MAIL_ICON} title="Revisa tu correo">
            <p className="mx-auto mt-2 max-w-[40ch] text-center text-[13px] leading-relaxed text-text-2">
              Si existe una cuenta con ese correo, te enviamos un enlace para restablecer tu
              contraseña. Vence en <b className="font-semibold text-text">{RESET_LINK_TTL}</b>.
            </p>
            {email && (
              <span className="mt-3.5 inline-block rounded-lg border border-line bg-surface-2 px-3 py-1.5 text-[12.5px] font-semibold text-text">
                {email}
              </span>
            )}
            <div className="mt-5">
              <Button variant="secondary" block loading={busy} onClick={() => void onResend()}>
                Reenviar enlace
              </Button>
              {resent && (
                <p role="status" className="mt-2 text-[11.5px] font-medium text-ok">
                  Listo, te enviamos el enlace de nuevo.
                </p>
              )}
            </div>
          </StateBlock>
          <BackToLogin />
        </>
      )}

      {view === 'set' && (
        <>
          <h1 className="text-center text-[22px] font-bold tracking-[-0.022em] text-text">
            Crea una contraseña nueva
          </h1>
          <p className="mx-auto mt-2 max-w-[40ch] text-center text-[13px] leading-relaxed text-text-2">
            Elígela bien: te la pediremos cada vez que entres a tu cuenta.
          </p>
          <form onSubmit={(e) => void onSetPassword(e)} className="mt-6" noValidate>
            <FormField label="Contraseña nueva" htmlFor="new-password">
              <PasswordInput
                autoComplete="new-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={busy}
                rules={PASSWORD_RULES.map((r) => ({ label: r.label, met: r.test(password) }))}
                {...(password.length > 0 ? { strength: strengthLevel(password) } : {})}
              />
            </FormField>
            <div className="mt-3.5">
              <FormField label="Confirmar contraseña" htmlFor="confirm-password">
                <PasswordInput
                  autoComplete="new-password"
                  required
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  disabled={busy}
                />
              </FormField>
              {confirm.length > 0 && !matches && (
                <p className="mt-1.5 text-[11.5px] font-medium text-sens">
                  Las contraseñas no coinciden.
                </p>
              )}
            </div>
            <Button
              type="submit"
              block
              className="mt-5"
              loading={busy}
              disabled={!policyOk || !matches}
            >
              Guardar y entrar
            </Button>
          </form>
        </>
      )}

      {view === 'done' && (
        <StateBlock tone="ok" icon={CHECK_ICON} title="Contraseña actualizada">
          <p className="mx-auto mt-2 max-w-[40ch] text-center text-[13px] leading-relaxed text-text-2">
            Tu contraseña se cambió correctamente. Por seguridad cerramos las demás sesiones
            abiertas.
          </p>
          <div className="mt-5">
            <Button block onClick={() => void navigate('/login')}>
              Ir a iniciar sesión
            </Button>
          </div>
        </StateBlock>
      )}

      {view === 'expired' && (
        <>
          <StateBlock tone="warn" icon={CLOCK_ICON} title="El enlace ya no sirve">
            <p className="mx-auto mt-2 max-w-[40ch] text-center text-[13px] leading-relaxed text-text-2">
              Los enlaces de recuperación vencen{' '}
              <b className="font-semibold text-text">{RESET_LINK_TTL}</b> después de enviarse, y
              solo se pueden usar una vez. Pide uno nuevo y te llega al instante.
            </p>
            <div className="mt-5">
              <Button block onClick={() => void navigate('/reset-password')}>
                Solicitar un enlace nuevo
              </Button>
            </div>
          </StateBlock>
          <BackToLogin />
        </>
      )}
    </AuthLayout>
  )
}
